import axios from 'axios';

/**
 * Boleto de uma parcela — leitura, nunca emissão.
 *
 * As parcelas continuam vindo do MEGA em `/plots`. Quando existe um boleto
 * emitido na CUB para a parcela, o backend sobrepõe três campos novos:
 *
 *   pdf_url             PDF do boleto (substitui o base64 do MEGA)
 *   pix_qr_code         payload copia-e-cola do PIX (NÃO é uma URL/imagem)
 *   billing_viewer_url  página da CUB com o boleto — **não usado**
 *
 * O `billing_viewer_url` continua chegando, mas a tela não o oferece: o
 * link para o ambiente da CUB foi retirado em 08/10/2026 por decisão da
 * Brasal. O cliente paga pelo PDF, pela linha digitável ou pelo PIX, sem
 * sair do Espaço do Cliente. Não reintroduzir sem combinar antes.
 *
 * Nenhum deles é obrigatório: parcela sem boleto na CUB vem com os três
 * nulos e o fluxo antigo (`/boleto-ws` -> SOAP -> base64) continua valendo.
 * Por isso tudo aqui é testado antes de usar.
 */

export function temPdf(parcela) {
    return Boolean(parcela && parcela.pdf_url);
}

export function temPix(parcela) {
    return Boolean(parcela && parcela.pix_qr_code);
}

/**
 * Abre o boleto da parcela em outra aba.
 *
 * Com `pdf_url` a aba abre na hora, sem request — o que além de mais rápido
 * evita o bloqueio de pop-up, já que a abertura acontece dentro do clique.
 * Sem ela, cai no `/boleto-ws` de sempre, que hoje pode responder tanto com
 * `PDF_URL` (boleto da CUB) quanto com `BOLETO` em base64 (boleto do MEGA).
 *
 * @returns {Promise<{ok: boolean, origem: string, erro?: string}>}
 */
export async function abrirBoleto(parcela, { contrato, agente } = {}) {
    if (temPdf(parcela)) {
        window.open(parcela.pdf_url, '_blank');
        return { ok: true, origem: 'cub' };
    }

    try {
        const { status, data } = await axios.post('/boleto-ws', {
            contract: contrato,
            agent: agente,
            code: parcela.par_in_codigo,
        });

        if (status !== 200 || !data) {
            return { ok: false, origem: 'desconhecida', erro: 'Boleto não encontrado.' };
        }

        if (data.ERRO) {
            return { ok: false, origem: data.ORIGEM || 'mega', erro: data.DESCRICAO || 'Boleto não encontrado.' };
        }

        if (data.PDF_URL) {
            window.open(data.PDF_URL, '_blank');
            return { ok: true, origem: data.ORIGEM || 'cub' };
        }

        if (data.BOLETO) {
            abrirPdfBase64(data.BOLETO);
            return { ok: true, origem: data.ORIGEM || 'mega' };
        }

        return { ok: false, origem: data.ORIGEM || 'desconhecida', erro: 'Boleto não encontrado.' };
    } catch (error) {
        return { ok: false, origem: 'desconhecida', erro: 'Erro ao buscar boleto.' };
    }
}

/**
 * Copia o PIX copia-e-cola da parcela.
 *
 * `pix_qr_code` é o payload EMV — a string que se cola no app do banco —, e
 * não a imagem do QR Code nem um link para ela.
 */
export function copiarPix(parcela) {
    if (!temPix(parcela)) {
        return Promise.resolve(false);
    }

    return copiar(parcela.pix_qr_code);
}

/**
 * Copia a linha digitável. Vem do MEGA ou, havendo boleto na CUB, da CUB —
 * o backend já resolveu isso antes de mandar a parcela.
 */
export function copiarLinhaDigitavel(parcela) {
    if (!parcela || !parcela.barcode) {
        return Promise.resolve(false);
    }

    return copiar(parcela.barcode);
}

async function copiar(texto) {
    try {
        await navigator.clipboard.writeText(texto);
        return true;
    } catch (error) {
        return false;
    }
}

function abrirPdfBase64(base64) {
    const bytes = atob(base64);
    const numeros = new Array(bytes.length);

    for (let i = 0; i < bytes.length; i++) {
        numeros[i] = bytes.charCodeAt(i);
    }

    const blob = new Blob([new Uint8Array(numeros)], { type: 'application/pdf' });

    window.open(URL.createObjectURL(blob), '_blank');
}
